import { Target, VariantLabels } from 'framer-motion'
import React, { PropsWithChildren, createContext, useContext } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

export type SlideDirection = 'up' | 'down' | 'left' | 'right'
export type TransitionType = 'page' | 'dialog'

interface AnimationProps {
  initial: boolean | Target | VariantLabels
  animate: boolean | Target | VariantLabels
}

interface INavigationState {
  type: TransitionType
  direction: SlideDirection
  animationProps: AnimationProps
  backPath?: string
  params?: any
}

interface ITransitionValue {
  navigate: (
    toPath: string,
    transitionType: TransitionType,
    direction: SlideDirection,
    backPath?: string,
    params?: {}
  ) => void
  state: INavigationState
}

export const TransitionContext = createContext<ITransitionValue>({
  navigate: () => {},
  state: {
    type: 'page',
    direction: 'up',
    animationProps: { initial: {}, animate: {} },
  },
})

export const TransitionProvider = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate()
  const { state } = useLocation()

  const handleTransitionNavigate = (
    path: string,
    type: TransitionType,
    direction: SlideDirection,
    backPath?: string,
    params?: any
  ) => {
    navigate(state?.backPath ?? path, {
      state: {
        type,
        direction,
        animationProps: getAnimationValues(direction),
        backPath,
        params,
      },
    })
  }

  const getAnimationValues = (direction: SlideDirection): AnimationProps => {
    const borderRadius = {
      borderTopLeftRadius: 16,
      borderTopRightRadius: 16,
    }

    switch (direction) {
      case 'up':
        return {
          initial: {
            marginTop: window.innerHeight,
            opacity: 0,
            ...borderRadius,
          },
          animate: {
            marginTop: 0,
            opacity: 1,
            ...borderRadius,
          },
        }
      case 'down':
        return {
          initial: {
            marginTop: window.innerHeight,
            opacity: 0,
            ...borderRadius,
          },
          animate: { marginTop: 0, opacity: 1, ...borderRadius },
        }
      case 'left':
        return {
          initial: {
            marginLeft: window.innerWidth,
            opacity: 0,
            ...borderRadius,
          },
          animate: { marginLeft: 0, opacity: 1, ...borderRadius },
        }
      case 'right':
        return {
          initial: {
            marginLeft: 0 - window.innerWidth,
            opacity: 0,
            ...borderRadius,
          },
          animate: { marginLeft: 0, opacity: 1, ...borderRadius },
        }
    }
  }

  return (
    <TransitionContext.Provider value={{ navigate: handleTransitionNavigate, state }}>
      {children}
    </TransitionContext.Provider>
  )
}

export const useTransitionNavigation = () => useContext(TransitionContext)
